import { createContext, ReactElement, ReactNode, useContext, useMemo } from 'react'

import { useContactPersons } from '../../../../../hooks/useContactPersons'
import { ContactPerson } from '../../../../contacts/types/contactPerson'
import { ZervantLocale } from '../../../../invoices/enums/zervantLocale'
import { InvoiceDocumentType } from '../../../enums/invoiceDocumentType'
import { SendAsEmailModalView } from '../types/sendAsEmailModalView'

interface ContextState {
  documentLocale?: ZervantLocale
  documentType: InvoiceDocumentType
  invoiceContactPersons: ContactPerson[]
  invoiceNumber: string
  isLoading: boolean
  view: SendAsEmailModalView
}

const SendAsEmailModalContext = createContext<ContextState | undefined>(undefined)

interface SendAsEmailModalContextProviderProps {
  children?: ReactNode
  contactId?: string | undefined
  documentLocale?: ZervantLocale
  documentType: InvoiceDocumentType
  invoiceNumber: string
  view: SendAsEmailModalView
}

export const SendAsEmailModalContextProvider = ({
  children,
  contactId,
  documentLocale,
  documentType,
  invoiceNumber,
  view,
}: SendAsEmailModalContextProviderProps): ReactElement => {
  const { contactPersons, isLoading } = useContactPersons({ contactId })

  const contextValue = useMemo(
    () => ({ documentLocale, documentType, invoiceContactPersons: contactPersons, invoiceNumber, isLoading, view }),
    [contactPersons, documentLocale, documentType, invoiceNumber, isLoading, view],
  )

  return <SendAsEmailModalContext.Provider value={contextValue}>{children}</SendAsEmailModalContext.Provider>
}

export const useSendAsEmailModal = () => {
  const context = useContext(SendAsEmailModalContext)

  if (!context) {
    throw new Error('SendAsEmailModalContextProvider is missing in the module!')
  }

  return context
}
